import React, { useState, useRef, useEffect, forwardRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import { useDidUpdateEffect } from 'ui_system/hooks/useDidUpdateEffect';

import { Theme } from 'ui_system/theme';
import CheckmarkIcn from 'views/ui/icons/checkmark-icon';

const padding = 20;

const NoticeContainer = styled.div`
  background: ${(props) => props.color};
  color: ${(props) => (props.textColor ? props.textColor : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  position: sticky;
  top: ${(props) => props.top}px;
  z-index: 1000;

  overflow: hidden;
  transition: all ${(props) => props.animationSpeed}ms ease-in-out;
  height: auto;
  opacity: 0;

  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: ${padding}px;
  padding-right: ${padding}px;

  font-size: 18px;
  font-weight: 300;
  line-height: 1.2;
`;

const Circle = styled.div`
  border-radius: 50%;
  border: 1px solid white;
  min-width: 30px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
`;

const CheckmarkIcon = styled(CheckmarkIcn)`
  fill: white;
  height: 12px;
`;

// Adapted from: https://css-tricks.com/using-css-transitions-auto-dimensions/
// TLDR: expanding/collapsing a dynamically sized element smoothly while maintaining reflow of surrounding elements
// is not possible with CSS alone, so we need to use JS with requestAnimationFrame to trigger the animation with the
// calculated height of the element.
// Note: this could be used in other contexts as well, so would be good to factor these functions out in that event.
function collapse(element) {
  // get the height of the element's inner content, regardless of its actual size
  const sectionHeight = element.scrollHeight;

  // temporarily disable all css transitions
  const elementTransition = element.style.transition;
  element.style.transition = '';

  // on the next frame (as soon as the previous style change has taken effect),
  // explicitly set the element's height to its current pixel height, so we
  // aren't transitioning out of 'auto'
  requestAnimationFrame(function () {
    element.style.transition = elementTransition;
    element.style.height = `${sectionHeight}px`;

    // on the next frame (as soon as the previous style change has taken effect),
    // have the element transition to height: 0
    requestAnimationFrame(function () {
      element.style.height = 0;
      element.style.opacity = 0;
      element.style.paddingTop = 0;
      element.style.paddingBottom = 0;
    });
  });
}

function expand(element) {
  // get the height of the element's inner content, regardless of its actual size
  const sectionHeight = element.scrollHeight;

  // temporarily disable all css transitions
  const elementTransition = element.style.transition;
  element.style.transition = '';

  requestAnimationFrame(function () {
    element.style.transition = elementTransition;
    element.style.height = 0;

    // on the next frame (as soon as the previous style change has taken effect)
    requestAnimationFrame(function () {
      // have the element transition to the height of its inner content
      element.style.height = `${sectionHeight + padding * 2}px`;
      element.style.opacity = 1;
      element.style.paddingTop = `${padding}px`;
      element.style.paddingBottom = `${padding}px`;

      // when the next css transition finishes (which should be the one we just triggered)
      element.addEventListener('transitionend', function listener(e) {
        // remove this event listener so it only gets triggered once
        element.removeEventListener('transitionend', listener);

        // remove "height" from the element's inline styles, so it can return to its initial value
        element.style.height = null;
      });
    });
  });
}

const NoticeElement = forwardRef(
  ({ animationSpeed, type, text, onHide, duration, top, showIcon }, ref) => {
    useEffect(() => {
      expand(ref.current);

      // Internally call a top level defined onHide function if specified, so that timeout logic only has to be dealt with once.
      if (onHide) {
        const timeoutID = setTimeout(() => {
          onHide();
        }, duration);

        return () => {
          clearTimeout(timeoutID);
          onHide();
        };
      }
    }, []);

    // Todo: Needs an 'error' icon
    const icon = <CheckmarkIcon />;

    let color = Theme.colors.lightText;
    if (type === 'success') color = Theme.colors.green;
    if (type === 'error') color = Theme.colors.darkRed;
    if (type === 'warning') color = Theme.colors.orange;

    return (
      <NoticeContainer
        ref={ref}
        animationSpeed={animationSpeed}
        color={color}
        textColor={type === 'warning' && Theme.colors.black}
        top={top}
      >
        {text && showIcon && <Circle>{icon}</Circle>}
        {text}
      </NoticeContainer>
    );
  }
);

export const Notice = ({
  text,
  type,
  show,
  onHide,
  duration,
  animationSpeed,
  top,
}) => {
  const [isVisible, setIsVisible] = useState(show);
  const [savedText, setSavedText] = useState(text);
  const containerRef = useRef(null);

  const handleClose = () => {
    collapse(containerRef.current);
    setTimeout(() => {
      setIsVisible(false);
    }, animationSpeed);
  };

  // Similar logic to ReactModal
  useDidUpdateEffect(() => {
    if (show) {
      setIsVisible(true);
    } else {
      handleClose();
    }
  }, [show]);

  // Deals with situation where we use the presence of text to determine whether to show the notice.
  // Basically, we always save the previous text, so that it doesn't disappear when collapsing.
  useDidUpdateEffect(() => {
    if (text) {
      setSavedText(text);
    }
  }, [text]);

  return isVisible ? (
    <NoticeElement
      ref={containerRef}
      animationSpeed={animationSpeed}
      type={type}
      text={savedText}
      onHide={onHide}
      duration={duration}
      top={top}
    />
  ) : null;
};

Notice.defaultProps = {
  text: '',
  show: false,
  type: 'notice',
  onHide: null,
  duration: 3000,
  animationSpeed: 333,
  top: 0,
  showIcon: true,
};

Notice.propTypes = {
  text: PropTypes.string,
  show: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['notice', 'error', 'success', 'warning']),
  onHide: PropTypes.func,
  duration: PropTypes.number,
  animationSpeed: PropTypes.number,
  top: PropTypes.number,
};

export default Notice;
